import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useActiveFamily } from './ActiveFamilyContext';
import { getFamilyPINConfig, setRequirePIN as setRequirePINFirestore, migratePINFromLocalStorage } from '../services/familyPINService';

type FamilyModeContextType = {
  enabled: boolean;
  enable: () => void;
  disable: () => void;
  disableDirectly: () => void; // Para uso interno del ExitModal sin validación PIN
  toggle: () => void;
  exitModalOpen: boolean;
  openExitModal: () => void;
  closeExitModal: () => void;
  // Configuración de filtro para modo grupo
  groupModeFilter: 'all' | 'current'; // 'all' = todas las tareas, 'current' = solo horario en curso
  setGroupModeFilter: (filter: 'all' | 'current') => void;
  configModalOpen: boolean;
  openConfigModal: () => void;
  closeConfigModal: () => void;
  // Sistema PIN parental
  pinModalOpen: boolean;
  pinModalMode: 'setup' | 'verify';
  openPINModal: (mode: 'setup' | 'verify', onSuccess: () => void) => void;
  closePINModal: () => void;
  hasPINSetup: boolean;
  requirePIN: boolean;
  setRequirePIN: (require: boolean) => void;
};

const FamilyModeContext = createContext<FamilyModeContextType | undefined>(undefined);

export const FamilyModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {

  // Inicializar desde localStorage de forma síncrona
  const [enabled, setEnabled] = useState<boolean>(() => {
    try {
      return localStorage.getItem("family_mode") === "1";
    } catch {
      return false;
    }
  });

  const [exitModalOpen, setExitModalOpen] = useState<boolean>(false);
  const [configModalOpen, setConfigModalOpen] = useState<boolean>(false);
  
  // Preferencia de filtro para modo grupo (por defecto 'all' = todas las tareas)
  const [groupModeFilter, setGroupModeFilter] = useState<'all' | 'current'>(() => {
    try {
      const saved = localStorage.getItem("family_mode_filter");
      return saved === 'current' ? 'current' : 'all';
    } catch {
      return 'all';
    }
  });

  // Sistema PIN parental (ahora desde Firestore)
  const { activeFamily } = useActiveFamily();
  const [pinModalOpen, setPinModalOpen] = useState<boolean>(false);
  const [pinModalMode, setPinModalMode] = useState<'setup' | 'verify'>('verify');
  const [pinSuccessCallback, setPinSuccessCallback] = useState<(() => void) | null>(null);
  const [hasPINSetup, setHasPINSetup] = useState<boolean>(false);
  const [requirePIN, setRequirePINState] = useState<boolean>(false);
  const [pinLoading, setPinLoading] = useState<boolean>(true);

  // Mantener sincronizado entre pestañas/ventanas
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === "family_mode") {
        setEnabled(e.newValue === "1");
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // Guardar cambios en localStorage
  useEffect(() => {
    if (enabled) localStorage.setItem("family_mode", "1");
    else localStorage.removeItem("family_mode");
  }, [enabled]);
  
  // Guardar preferencia de filtro
  useEffect(() => {
    try {
      localStorage.setItem("family_mode_filter", groupModeFilter);
    } catch {}
  }, [groupModeFilter]);

  // Cargar configuración PIN desde Firestore cuando cambia la familia activa
  useEffect(() => {
    const loadPINConfig = async () => {
      if (!activeFamily?.id) {
        setHasPINSetup(false);
        setRequirePINState(false);
        setPinLoading(false);
        return;
      }

      try {
        setPinLoading(true);
        
        // Intentar migrar de localStorage si existe
        const migrated = await migratePINFromLocalStorage(activeFamily.id);
        
        // Cargar configuración desde Firestore
        const config = await getFamilyPINConfig(activeFamily.id);
        setHasPINSetup(!!config.parentalPIN);
        setRequirePINState(config.requirePIN);
        
        if (migrated) {
          console.log('✅ PIN migrado de localStorage a Firestore');
        }
      } catch (error) {
        console.error('Error al cargar configuración PIN:', error);
        setHasPINSetup(false);
        setRequirePINState(false);
      } finally {
        setPinLoading(false);
      }
    };

    loadPINConfig();
  }, [activeFamily?.id]);

  // Recargar configuración PIN cuando se cierra el modal (por si se configuró/eliminó)
  useEffect(() => {
    const reloadPINConfig = async () => {
      if (!pinModalOpen && activeFamily?.id) {
        try {
          const config = await getFamilyPINConfig(activeFamily.id);
          setHasPINSetup(!!config.parentalPIN);
          setRequirePINState(config.requirePIN);
        } catch (error) {
          console.error('Error al recargar configuración PIN:', error);
        }
      }
    };
    
    reloadPINConfig();
  }, [pinModalOpen, activeFamily?.id]);

  const openPINModal = (mode: 'setup' | 'verify', onSuccess: () => void) => {
    setPinModalMode(mode);
    setPinSuccessCallback(() => onSuccess);
    setPinModalOpen(true);
  };

  const closePINModal = () => {
    setPinModalOpen(false);
    setPinSuccessCallback(null);
  };

  const handlePINSuccess = () => {
    if (pinSuccessCallback) {
      pinSuccessCallback();
    }
    closePINModal();
  };

  const enableWithPIN = () => {
    // SIEMPRE activar directamente (PIN solo para salir)
    setEnabled(true);
  };

  const disableWithPIN = () => {
    if (requirePIN && hasPINSetup) {
      // Requiere PIN para desactivar
      openPINModal('verify', () => setEnabled(false));
    } else {
      // Desactivar directamente
      setEnabled(false);
    }
  };

  // Wrapper para setRequirePIN que actualiza Firestore
  const setRequirePIN = async (require: boolean) => {
    if (!activeFamily?.id) return;
    
    try {
      await setRequirePINFirestore(activeFamily.id, require);
      setRequirePINState(require);
    } catch (error) {
      console.error('Error al actualizar requirePIN:', error);
    }
  };

  const value = useMemo(
    () => ({
      enabled,
      enable: enableWithPIN,
      disable: disableWithPIN,
      disableDirectly: () => setEnabled(false), // Desactivar sin validación (para ExitModal)
      toggle: () => (enabled ? disableWithPIN() : enableWithPIN()),
      exitModalOpen,
      openExitModal: () => setExitModalOpen(true),
      closeExitModal: () => setExitModalOpen(false),
      groupModeFilter,
      setGroupModeFilter: (filter: 'all' | 'current') => setGroupModeFilter(filter),
      configModalOpen,
      openConfigModal: () => setConfigModalOpen(true),
      closeConfigModal: () => setConfigModalOpen(false),
      pinModalOpen,
      pinModalMode,
      openPINModal,
      closePINModal: closePINModal,
      hasPINSetup,
      requirePIN,
      setRequirePIN,
    }),
    [enabled, exitModalOpen, groupModeFilter, configModalOpen, pinModalOpen, pinModalMode, hasPINSetup, requirePIN, activeFamily?.id]
  );

  return <FamilyModeContext.Provider value={value}>{children}</FamilyModeContext.Provider>;
};

export const useFamilyMode = (): FamilyModeContextType => {
  const ctx = useContext(FamilyModeContext);
  if (!ctx) throw new Error("useFamilyMode must be used within FamilyModeProvider");
  return ctx;
};
